import { type ButtonProps, DesignSystemButton } from '@mathflat/design-system-2024'
import clsx from 'clsx'
import { forwardRef } from 'react'

import { Icon } from '@/component/atoms/Icon'
import type * as icons from '@/component/atoms/Icon/svg/index'

import IconButton from '../IconButton/IconButton'
import { buttonStyle } from './Button.style'

type Props = {
  leftIcon?: keyof typeof icons
  rightIcon?: keyof typeof icons
}

/**
 * 버튼의 텍스트는 children으로 전달하며, ReactNode가 아닌 string만 허용하는 것이 팀 내 컨벤션입니다.
 * props로 buttonText를 따로 받지 않고, 보다 React스러운 방식으로 children을 사용하는 것으로 FE에서 결정하였습니다.
 */

const iconSize: Record<NonNullable<ButtonProps['size']>, number> = {
  xs: 16,
  s: 16,
  m: 20,
  l: 20,
  xl: 20,
  '2xl': 22,
  '3xl': 22,
}

const Button = forwardRef<HTMLButtonElement, ButtonProps & Props>((props, ref) => {
  const {
    size = 'm',
    variant = 'solid',
    color = 'accent',
    width = 'auto',
    leftIcon,
    rightIcon,
    type,
    children,
    ...rest
  } = props

  const buttonClassName = clsx('button', `button-${size}`, `button-${variant}`, `button-${color}`)

  return (
    <DesignSystemButton
      type={type}
      css={buttonStyle}
      className={buttonClassName}
      width={width}
      size={size}
      ref={ref}
      {...rest}
    >
      {leftIcon && <Icon name={leftIcon} size={iconSize[size]} />}
      {children}
      {rightIcon && <Icon name={rightIcon} size={iconSize[size]} />}
    </DesignSystemButton>
  )
}) as React.ForwardRefExoticComponent<
  React.PropsWithoutRef<ButtonProps & Props> & React.RefAttributes<HTMLButtonElement>
> & { Icon: typeof IconButton }

Button.displayName = 'Button'
Button.Icon = IconButton

export default Button
